/*
 |--------------------------------------------------------------------------
 | Drawer
 |
 | This module adds functionality to the Sitecore and Angular version of
 | the drawer component
 |--------------------------------------------------------------------------
 */

class ADrawer {
  constructor(element) {
    this.element = element;
  }

  closingTimer;
  triggeredCloseElement = false;

  drawerStateList = {
    closing: 'closing',
    close: 'close',
    opening: 'opening',
    open: 'open',
  };

  init = () => {
    // Set initial state
    if (this.element.open) {
      this.element.dataset.drawerState = this.drawerStateList.open;
    } else {
      this.element.dataset.drawerState = this.drawerStateList.close;
    }

    this.element.classList.add('a-drawer');

    // Handle animations
    this.element.addEventListener('animationend', this.animationEnd);

    // Cancel/Escape
    this.element.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    // Close listeners
    this.element.addEventListener('mousedown', this.onMouseDown);
    this.element.addEventListener('mouseup', this.onMouseUp);

    // Trap focus
    var focusableEls = this.element.querySelectorAll(
      ':is(a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])):not([disabled])'
    );
    var firstFocusableEl = focusableEls[0];
    var lastFocusableEl = focusableEls[focusableEls.length - 1];

    this.element.addEventListener('keydown', function (e) {
      var KEYCODE_TAB = 9;

      var isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

      if (!isTabPressed) {
        return;
      }

      if (e.shiftKey) {
        /* shift + tab */ if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    });
  };

  show = () => {
    if (this.element.open) {
      return;
    }

    const modal = this.element.getAttribute('aria-modal');
    this.element.dataset.drawerState = this.drawerStateList.opening;
    this.element.dispatchEvent(new Event('drawerOpening'));

    if (modal === 'true') {
      this.element.showModal();
    } else {
      this.element.show();
    }
  };

  onMouseDown = (event) => {
    this.triggeredCloseElement = false;
    // Close drawer when clicking outside
    if (event.target === event.currentTarget) {
      this.triggeredCloseElement = true;
    }
  };

  onMouseUp = (event) => {
    if (this.triggeredCloseElement) {
      if (event.target === event.currentTarget) {
        this.close();
      }
    }
  };

  close = () => {
    if (
      this.element.dataset.drawerState === this.drawerStateList.close ||
      this.element.dataset.drawerState === this.drawerStateList.closing
    ) {
      return;
    }

    this.element.dataset.drawerState = this.drawerStateList.closing;
    this.element.dispatchEvent(new Event('drawerClosing'));

    // Fallback in case the animationend event doesn't fire
    closingTimer = setTimeout(this.afterClose, 500);
  };

  afterClose = () => {
    clearTimeout(closingTimer);
    this.element.dataset.drawerState = this.drawerStateList.close;
    this.element.close();
    if (this.element.getAttribute('aria-modal') === 'true') {
      document.body.classList.remove('modal-open');
    }
    this.element.dispatchEvent(new Event('drawerClose'));
  };

  afterOpen = () => {
    this.element.dataset.drawerState = this.drawerStateList.open;
    if (this.element.getAttribute('aria-modal') === 'true') {
      document.body.classList.add('modal-open');
    }
    this.element.dispatchEvent(new Event('drawerOpen'));
  };

  animationEnd = (event) => {
    switch (this.element.dataset.drawerState) {
      case this.drawerStateList.closing:
        this.afterClose();
        break;
      case this.drawerStateList.opening:
        this.afterOpen();
        break;
    }
  };
}

// Module selector
const ADrawerSelector = 'dialog.a-drawer';

// Initiate module
function ADrawerCreateInstance(element) {
  if (!element.drawerClass) {
    element.drawerClass = new ADrawer(element);
    element.drawerClass.init();
  }
}

// Create new Drawer instances on DOM mutation
document.querySelectorAll(`${ADrawerSelector}`).forEach(ADrawerCreateInstance);

const ADrawerObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${ADrawerSelector}`)
        .forEach(ADrawerCreateInstance);
    });
  });
});

ADrawerObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});

// Global open drawer function
window.showDrawer = (drawerId) => {
  drawer = document.getElementById(drawerId);
  if (drawer === null) {
    throw new Error('No drawer found with id="' + drawerId + '".');
  }

  drawer.drawerClass.show();
};

// Global close drawer function
window.closeDrawer = (drawerId) => {
  drawer = document.getElementById(drawerId);
  if (drawer === null) {
    throw new Error('No drawer found with id="' + drawerId + '".');
  }

  drawer.drawerClass.close();
};
